import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import "./ProgressCircle.css";
import { DateTimeUtils } from "../../utils/datetime.utils"; // Importing the function
import CircleProgressBar from "./CircleProgressBar";
import { TimerControl } from "../../types/timer.type";
import { AppTheme } from "../../constants/theme";

interface ProgressCircleProps {
  initialTotalTime: number;
  color?: "red" | "green" | "yellow" | "default";
  sound?: boolean;
  bypassCacheGuid?: string;
  icon?: string;
  label?: string;
  customValue?: any;
  type?: "solid" | "segmented";
}

const ProgressCircle = forwardRef<TimerControl, ProgressCircleProps>(({
  initialTotalTime,
  color,
  sound = true,
  bypassCacheGuid,
  label,
  icon,
  customValue,
  type = "segmented"
}, ref) => {

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const [totalTime, setTotalTime] = useState(initialTotalTime);
  const [timeLeft, setTimeLeft] = useState(initialTotalTime);
  const [paused, setPaused] = useState(false);
  const [bgColor, setBgColor] = useState("#E0E0E0");
  const [progressColor, setProgressColor] = useState("#FF5733");
  const [fontSize, setFontSize] = useState("var(--title-hero-font-size)");
  const audioRef = useRef<HTMLAudioElement | null>(null); // Reference to audio element
  const [_, setCacheGuid] = useState(bypassCacheGuid);

  const updateColorsAndFontSize = useCallback(() => {
    setFontSize(
      timeLeft > 3600
        ? "var(--font-fontSize-3xl, 40px)"
        : "var(--font-fontSize-3xl, 72px)"
    );
  }, [timeLeft]);

  useEffect(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current); // Clear any existing interval when initialTotalTime changes
    }

    const delayStart = setTimeout(() => {
      intervalIdRef.current = setInterval(() => {
        if (!paused) {
          setTimeLeft((prevTime) => Math.max(prevTime - 1, 0));
        }
      }, 1000);
    }, 150);

    return () => {
      clearTimeout(delayStart); // Clear the timeout if the component unmounts
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current); // Clear the interval if the component unmounts
      }
    };
  }, [initialTotalTime, paused]);

  useEffect(() => {
    updateColorsAndFontSize();

    if (sound && timeLeft == 3 && audioRef.current) {
      audioRef?.current?.play();
    }

  }, [timeLeft, updateColorsAndFontSize]);

  useEffect(() => {
    setCacheGuid(bypassCacheGuid);
    setTotalTime(initialTotalTime);
    setTimeLeft(initialTotalTime);
  }, [initialTotalTime, bypassCacheGuid]);

  // useImperativeHandle exposes functions to parent components via ref
  useImperativeHandle(ref, () => ({
    stop: () => setPaused(true),
    play: () => setPaused(false),
    resume: () => setPaused(false),
    reset: () => {
      setPaused(false);
      setTimeLeft(initialTotalTime);
      setTotalTime(initialTotalTime);
    },
    setTime: (newTime: number) => {
      setPaused(true);
      setTimeLeft(newTime);
      setTotalTime(newTime);
      setPaused(false);
    },
  }));


  useEffect(() => {
    if (color === "default" || !color) {
      // Apply default colors here
      setBgColor("#E0E0E0");
      setProgressColor(AppTheme.active_color);
    }
    else if (color === "red") {
      setBgColor("#E0E0E0");
      setProgressColor("#ED6769");
    } else if (color === "green") {
      setBgColor("#E0E0E0");
      setProgressColor("#2B5A38");
    } else if (color === "yellow") {
      setBgColor("#E0E0E0");
      setProgressColor("#836C18");
    } else {
      // Default color case
      setBgColor("#0B0B0B");
      setProgressColor(AppTheme.active_color);
    }
  }, [color, timeLeft, initialTotalTime]);



  const progress = totalTime ? ((totalTime - timeLeft) / totalTime) * 100 : 0;

  const circleColorClass =
    color === "yellow" || (color === "red" && timeLeft < 10)
      ? "content yellow"
      : color === "green"
        ? "content green"
        : color === "red"
          ? "content red"
          : "content";

  return (
    <div key={bypassCacheGuid}>
      <div className="progress-circle">
        <div className={circleColorClass}>
          <div className="element">
            <CircleProgressBar
              progress={progress}
              bgColor={bgColor}
              progressColor={progressColor}
              type={type}
            />
            <div className="overlap-group">
              <div className="text-wrapper-container">
                <img alt="Workout Icon" src={icon ? icon : '/img/flame.svg'} width="56" height="56" />

                <div className="div" style={{ fontSize }}>
                  {customValue ?
                    customValue
                    : DateTimeUtils.formatTime(timeLeft)}
                </div>

                {label && <div className="div timer-label">
                  {label}
                </div>
                }
              </div>
            </div>
          </div>
        </div>
        <audio ref={audioRef} src="/sound/ding_3s.wav" preload="auto" />
      </div>
    </div>
  );
}
);

export default ProgressCircle; 